import { useState } from 'react';
import { format } from 'date-fns';
import { th } from 'date-fns/locale';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { SuddenLeaveReport } from '@/hooks/useSuddenLeaveReports';

interface SuddenLeaveConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  report: SuddenLeaveReport | null;
  onConfirm: (action: 'PAY' | 'NOT_PAY', note: string) => Promise<void>;
}

export function SuddenLeaveConfirmDialog({
  open,
  onOpenChange,
  report,
  onConfirm,
}: SuddenLeaveConfirmDialogProps) {
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!report) return null;

  const handleConfirm = async (action: 'PAY' | 'NOT_PAY') => {
    setIsSubmitting(true);
    try {
      await onConfirm(action, note);
      setNote('');
      onOpenChange(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setNote('');
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>ตรวจสอบการจ่ายเงินคลาส</DialogTitle>
          <DialogDescription>
            กรุณาเลือกสถานะการจ่ายเงินสำหรับคลาสนี้
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div className="text-muted-foreground">นักเรียน:</div>
            <div className="font-medium">
              {report.student_nickname} ({report.student_full_name})
            </div>
            <div className="text-muted-foreground">วันที่:</div>
            <div className="font-medium">
              {format(new Date(report.teaching_date), 'dd MMMM yyyy', { locale: th })}
            </div>
            <div className="text-muted-foreground">ครู:</div>
            <div className="font-medium">{report.teacher_display || report.teacher_id}</div>
            <div className="text-muted-foreground">วิชา:</div>
            <div className="font-medium">{report.subject}</div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">หมายเหตุ (ไม่บังคับ)</Label>
            <Textarea
              id="note"
              placeholder="ระบุหมายเหตุหรือเหตุผล..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2">
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isSubmitting}>
            ยกเลิก
          </Button>
          <Button
            variant="default"
            className="bg-green-600 hover:bg-green-700"
            onClick={() => handleConfirm('PAY')}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'กำลังบันทึก...' : 'ยืนยันจ่ายปกติ'}
          </Button>
          <Button
            variant="destructive"
            onClick={() => handleConfirm('NOT_PAY')}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'กำลังบันทึก...' : 'ยืนยันไม่จ่าย'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
